import { useEffect, useRef, useState } from "react";
import { getApiUrl } from "../api";
import { getStoredToken, getStoredUser, redirectToLogin } from "../auth";
import { getCloudflareImageUrl } from "../utils/cloudflareImages";

const UAE_PASS_STATE_KEY = "tarahumUaePassState";
const UAE_PASS_NONCE_KEY = "tarahumUaePassNonce";
const UAE_PASS_RETURN_KEY = "tarahumUaePassReturnTo";
const UAE_PASS_RESTRICTED_KEY = "tarahumUaePassRestricted";
const UAE_PASS_RESTRICTED_PROFILE_KEY = "tarahumUaePassRestrictedProfile";

const makeRandom = () => {
  const bytes = new Uint8Array(16);
  window.crypto?.getRandomValues?.(bytes);
  if (!bytes.some(Boolean)) return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
};

export default function BeneficiarySidebarProfile({ onVerificationChange, language = "en" }) {
  const isArabic = language === "ar";
  const inputRef = useRef(null);
  const storedUser = getStoredUser();
  const [profile, setProfile] = useState({
    fullname: storedUser?.userName || storedUser?.user || "",
    ImagePath: storedUser?.profile?.ImagePath || "",
  });
  const [userId, setUserId] = useState(storedUser?.userId || "");
  const [isUploading, setIsUploading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isUaePass, setIsUaePass] = useState(
    String(storedUser?.profile?.regfrom || "").toLowerCase() === "uaepass"
  );
  const [message, setMessage] = useState("");
  const [isError, setIsError] = useState(false);

  const token = getStoredToken();
  const imageUrl = getCloudflareImageUrl(
    profile.ImagePath,
    "w=240,h=240,fit=cover,sharpen=1"
  );

  const verifyWithUaePass = () => {
    setMessage("");
    const authorizeUrl = process.env.REACT_APP_UAE_PASS_AUTHORIZE_URL ||
      "https://id.uaepass.ae/idshub/authorize";
    const clientId = process.env.REACT_APP_UAE_PASS_CLIENT_ID || "tarahum_webr_prod";
    const redirectUri = process.env.REACT_APP_UAE_PASS_REDIRECT_URI ||
      `${window.location.origin}/uae-pass-callback`;
    const scope = process.env.REACT_APP_UAE_PASS_SCOPE || "urn:uae:digitalid:profile:general";
    const acrValues = process.env.REACT_APP_UAE_PASS_ACR_VALUES ||
      "urn:safelayer:tws:policies:authentication:level:low";

    if (!clientId || !redirectUri) {
      setIsError(true);
      setMessage("UAE PASS verification is not configured.");
      return;
    }

    setIsVerifying(true);
    const state = `uaepass_${makeRandom()}`;
    const nonce = makeRandom();
    window.sessionStorage.removeItem(UAE_PASS_RESTRICTED_KEY);
    window.sessionStorage.removeItem(UAE_PASS_RESTRICTED_PROFILE_KEY);
    window.sessionStorage.setItem(UAE_PASS_STATE_KEY, state);
    window.sessionStorage.setItem(UAE_PASS_NONCE_KEY, nonce);
    window.sessionStorage.setItem(UAE_PASS_RETURN_KEY, "/beneficent-dashboard");

    const params = new URLSearchParams({
      response_type: "code",
      client_id: clientId,
      redirect_uri: redirectUri,
      scope,
      state,
      nonce,
    });
    if (acrValues) params.set("acr_values", acrValues);
    window.location.assign(`${authorizeUrl}?${params.toString()}`);
  };

  useEffect(() => {
    const controller = new AbortController();

    const loadProfile = async () => {
      try {
        const response = await fetch(getApiUrl("/api/BeneficiaryProfile.ashx"), {
          method: "GET",
          credentials: "include",
          headers: {
            Accept: "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          signal: controller.signal,
        });
        if (response.status === 401) {
          redirectToLogin();
          return;
        }
        const result = await response.json().catch(() => ({}));
        if (!response.ok || !result.success) return;
        setProfile((current) => ({ ...current, ...(result.profile || {}) }));
        setUserId(result.userId || storedUser?.userId || "");
        const verifiedByUaePass = result.isUaePass === true;
        setIsUaePass(verifiedByUaePass);
        onVerificationChange?.(verifiedByUaePass);
      } catch (error) {
        if (error.name !== "AbortError") setMessage("Unable to load profile image.");
      }
    };

    const handleProfileChanged = (event) => {
      if (event.detail?.profile) {
        setProfile((current) => ({ ...current, ...event.detail.profile }));
      }
    };

    loadProfile();
    window.addEventListener("beneficiary-profile:changed", handleProfileChanged);
    return () => {
      controller.abort();
      window.removeEventListener("beneficiary-profile:changed", handleProfileChanged);
    };
  }, [token, storedUser?.userId, onVerificationChange]);

  const uploadImage = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/png", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      setIsError(true);
      setMessage("Only JPG, PNG and WebP images are allowed.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setIsError(true);
      setMessage("The image must be 5 MB or smaller.");
      return;
    }

    setIsUploading(true);
    setIsError(false);
    setMessage("");

    try {
      const formData = new FormData();
      formData.append("image", file, file.name);
      const response = await fetch(getApiUrl("/api/BeneficiaryProfileImage.ashx"), {
        method: "POST",
        credentials: "include",
        headers: {
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: formData,
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result.success) {
        throw new Error(result.message || "Unable to upload the profile image.");
      }

      const updatedProfile = { ...profile, ImagePath: result.imagePath };
      setProfile(updatedProfile);
      setMessage(result.message || "Profile image updated successfully.");
      window.dispatchEvent(new CustomEvent("beneficiary-profile:changed", {
        detail: { profile: updatedProfile },
      }));
    } catch (error) {
      setIsError(true);
      setMessage(error.message || "Unable to upload the profile image.");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="beni-profile beneficiary-sidebar-profile">
      <div className={`db1-avatar pro-p beneficiary-avatar${isUploading ? " uploading" : ""}`}>
        {imageUrl ? <img src={imageUrl} alt={profile.fullname || ""} onError={(event) => { event.currentTarget.style.display = "none"; }} /> : null}
        <button
          type="button"
          className="beneficiary-avatar-change"
          onClick={() => inputRef.current?.click()}
          disabled={isUploading}
          aria-label="Change beneficiary profile image"
          title="Change profile image"
        >
          <i className={isUploading ? "fa-solid fa-spinner fa-spin" : "fa-solid fa-camera"}></i>
        </button>
        <input
          ref={inputRef}
          className="beneficiary-avatar-input"
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={uploadImage}
        />
      </div>

      {((isArabic ? profile.fullname_ar : null) || profile.fullname) ? <h2>{(isArabic ? profile.fullname_ar : null) || profile.fullname}</h2> : null}
      <p>{isArabic ? "رقم المستفيد:" : "Beneficiary ID :"} {userId ? `BEN-${userId}` : (isArabic ? "قيد الانتظار" : "Pending")}</p>
      <span className={`verify-tag${isUaePass ? "" : " unverified"}`}>
        <i className={`fa-solid ${isUaePass ? "fa-circle-check" : "fa-circle-exclamation"}`}></i>
        {isUaePass ? (isArabic ? "مستفيد موثق" : "Verified Beneficiary") : (isArabic ? "مستفيد غير موثق" : "Unverified Beneficiary")}
      </span>
      {!isUaePass ? (
        <button
          type="button"
          className="beneficiary-uae-pass-verify"
          onClick={verifyWithUaePass}
          disabled={isVerifying}
        >
          <img src="/images/img/uae-pass.png" alt="" />
          {isVerifying ? "Connecting..." : "Verify with UAE PASS"}
        </button>
      ) : null}
      {message ? (
        <small className={`beneficiary-avatar-message ${isError ? "error" : "success"}`}>
          {message}
        </small>
      ) : null}
    </div>
  );
}
